import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { CURRENCIES, formatMoney, todayISO } from '@/lib/format';
import { fetchGoalsBreakdown, flattenBreakdownRows } from '@/lib/currency';
import FxBreakdownCard from '@/components/FxBreakdownCard';
import { hasFeature, SOFT_LIMITS, effectivePlan } from '@/lib/entitlements';
import { UpsellPill } from '@/components/UpgradePrompt';
import { toast } from 'sonner';
import {
  Plus, Trash2, Target, Loader2,
  ShieldCheck, Sparkles, Home, GraduationCap, Plane, BriefcaseBusiness, Heart, Compass, Mountain,
  Star, Lightbulb, Link2, Pencil, CheckCircle2, RefreshCw,
} from 'lucide-react';

import usePageTitle from '@/hooks/usePageTitle';
import useFocusRefetch from '@/hooks/useFocusRefetch';
import { readCache, writeCache, withTimeout } from '@/lib/pageCache';

// Phase 84.27 v3.6.0 — Linked-goals onboarding + celebration.
import GoalsOnboardingModal   from '@/components/goals/GoalsOnboardingModal';
import GoalCelebrationModal   from '@/components/goals/GoalCelebrationModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// =============================================================================
// Phase 72.0 — Goal templates.
//
// Each template knows whether its goal is `manual` (user enters everything)
// or `linked` (computed from existing 2120 data). v1 ships ONE linked type
// — Debt Freedom → liabilities. Future phases add Emergency Fund (linked
// to expenses), FI (linked to FI engine), etc.
// =============================================================================
const TEMPLATES = [
  // Linked (powered by 2120 data)
  { kind: 'debt',      link_kind: 'debt_freedom',           title: 'Debt Freedom',          icon: Compass,           blurb: 'Lighter shoulders, clearer mind.',  linked: true },
  { kind: 'emergency', link_kind: 'emergency_fund',         title: 'Emergency Fund',        icon: ShieldCheck,       blurb: 'Months of calm, based on what you actually spend.', linked: true },
  { kind: 'fi',        link_kind: 'financial_independence', title: 'Financial Independence',icon: Mountain,          blurb: 'The long horizon. Tracked by 2120.', linked: true },
  // Manual (user defines everything)
  { kind: 'freedom',   link_kind: 'manual',         title: 'Freedom Fund',          icon: Sparkles,          blurb: 'Optionality. The fund that lets you say yes — or no.' },
  { kind: 'home',      link_kind: 'manual',         title: 'Home Down Payment',     icon: Home,              blurb: 'A place that’s yours.' },
  { kind: 'education', link_kind: 'manual',         title: 'Education Savings',     icon: GraduationCap,     blurb: 'For learning that compounds.' },
  { kind: 'family',    link_kind: 'manual',         title: 'Family Stability',      icon: Heart,             blurb: 'A buffer for the people who depend on you.' },
  { kind: 'travel',    link_kind: 'manual',         title: 'Travel Fund',           icon: Plane,             blurb: 'Memories worth saving for.' },
  { kind: 'sabbatical',link_kind: 'manual',         title: 'Sabbatical Fund',       icon: Sparkles,          blurb: 'A pause, planned with care.' },
  { kind: 'business',  link_kind: 'manual',         title: 'Business Fund',         icon: BriefcaseBusiness, blurb: 'Seed for something you’re building.' },
];

// Phase 72.1 — Months-of-runway picker for Emergency Fund. Industry rule
// of thumb: 3 months for dual-income / low-volatility, 6 for single-income,
// 9-12 for self-employed / variable income. We surface 4 calm options.
const RUNWAY_MONTHS = [
  { value: 3,  label: '3 months',  hint: 'Lean cushion (dual income, stable).' },
  { value: 6,  label: '6 months',  hint: 'Industry standard.' },
  { value: 9,  label: '9 months',  hint: 'Conservative.' },
  { value: 12, label: '12 months', hint: 'Self-employed / variable income.' },
];

// Phase 72.0.1 — Debt strategies. Decides which monthly-action line we
// surface on the goal card. "just_track" leaves the legacy 10% nudge as-is.
const DEBT_STRATEGIES = [
  { value: 'just_track',     label: 'Just track payoff',       hint: 'Watch the balance fall over time.' },
  { value: 'increase_emi',   label: 'Increase EMI each month', hint: 'Add a steady amount on top of your EMI.' },
  { value: 'one_time_extra', label: 'One-time lump-sum',       hint: 'I plan a single extra payment.' },
  { value: 'close_early',    label: 'Close early',             hint: 'Aggressive payoff — minimise total interest.' },
];

function monthsUntil(d) {
  if (!d) return null;
  const t = new Date(d).getTime() - Date.now();
  return Math.max(1, Math.round(t / (1000 * 60 * 60 * 24 * 30)));
}

function liabilityKindLabel(kind) {
  return ({
    home_loan: 'Home Loan',
    car_loan: 'Car Loan',
    personal_loan: 'Personal Loan',
    education_loan: 'Education Loan',
    other_debt: 'Other Debt',
  })[kind] || (kind || 'Loan');
}

// =============================================================================
// Phase 84.28.2 v3.6.4 — Wizard: country-aware instrument suggestions.
//
// Suggestion copy is deliberately opinionated and short. We do NOT try to
// enumerate every eligible product — that quickly becomes a comparison
// blog. Instead, we surface the ONE or TWO defaults that map cleanly to
// the goal's horizon (short/medium/long) in each country. The user can
// still link any account or holding they own from the picker below.
// =============================================================================
const INSTRUMENT_SUGGESTIONS = {
  IN: {
    emergency:  ['Liquid mutual fund or a high-interest savings account. Instant redemption, capital-preserving.'],
    freedom:    ['Ultra-short debt fund or arbitrage fund. Yield above savings, tax-friendly on 3yr+ holds.'],
    home:       ['Recurring deposit or short-duration debt fund if your buy is 1–3 years out. SIP into a balanced advantage fund for 3–5yr horizons.'],
    education:  ['SIP into a diversified equity mutual fund for 5yr+ horizons. Sukanya Samriddhi (girl child) or PPF for tax-anchored long compounding.'],
    family:     ['Term insurance for the income earner, plus a liquid buffer. Not a market instrument — protection first.'],
    travel:     ['Recurring deposit or a short-term debt fund. Predictable, calm, no surprises in the month you travel.'],
    sabbatical: ['Short-duration debt fund. You want the money accessible without market timing when you press pause.'],
    business:   ['Money-market or ultra-short debt fund. Keep the runway intact, earn a small yield while you build.'],
    retire:     ['NPS (National Pension System) plus a diversified equity SIP. Long horizons reward equity + tax anchoring.'],
    fi:         ['Equity index fund SIP plus NPS. FI is the long horizon — this is where compounding works hardest.'],
  },
  AE: {
    emergency:  ['A no-fee UAE savings account (Wio Save Space, Mashreq NEO Save, Liv). Instant access, no lock-in.'],
    freedom:    ['A high-yield savings pot or a short-duration sukuk ETF. Yield above 4% at time of writing, capital-preserving.'],
    home:       ['A savings space for 1–3yr buys. Diversified global equity ETF (via Sarwa/StashAway) for 3–5yr horizons.'],
    education:  ['Global equity index ETF via Sarwa or ADCB’s smart-investor platform. 5yr+ horizons compound best in equities.'],
    family:     ['Term life (Sun Life, Zurich) for the income earner + a liquid AED buffer. Protection first, market later.'],
    travel:     ['A dedicated savings space at your primary bank. Predictable, ring-fenced from month-to-month spend.'],
    sabbatical: ['A high-yield savings account or short-duration fund. Access matters more than yield when you pause working.'],
    business:   ['A separate current account plus a money-market fund for excess cash. Keep runway visible, earn a little yield.'],
    retire:     ['Global equity ETFs via Sarwa/StashAway plus a Sharia-compliant fund if that matters to you.'],
    fi:         ['Global equity index ETF (VWRA, VOO) via Sarwa/Interactive Brokers. FI is the long compound — buy the market.'],
  },
  US: {
    emergency:  ['HYSA (Ally, Marcus, Wealthfront cash) or a money-market fund. Instant access at 4%+ APY.'],
    freedom:    ['Treasury money market or short-duration Treasury ETF (SGOV). Higher yield than checking, no surprises.'],
    home:       ['HYSA or Treasury bills for <3yr buys. For 3–5yr, a balanced fund (60/40) via Vanguard/Fidelity.'],
    education:  ['529 plan (state tax benefits). For unrestricted use, a diversified index ETF (VOO/VTI) with a 5yr+ horizon.'],
    family:     ['Term life for the income earner + HYSA buffer. Protection layer first.'],
    travel:     ['Dedicated HYSA sub-account. Ally and Sofi both allow named buckets for zero-friction ring-fencing.'],
    sabbatical: ['HYSA plus a short-duration Treasury ETF. Liquidity is worth more than the extra 50 bps of yield.'],
    business:   ['Business HYSA or Treasury money market. Rare that operating cash should be in equities.'],
    retire:     ['401(k) up to the match, then IRA, then taxable index ETFs. Tax-anchored accounts first.'],
    fi:         ['Broad-market index ETF (VTI/VOO) in tax-anchored accounts first. The formula is old and it still works.'],
  },
};
// Fallback for countries we haven't tuned yet (GB, SG, AU, etc.). Uses
// generic index-fund language so we never render an empty suggestion.
const GENERIC_SUGGESTIONS = {
  emergency:  ['A high-interest savings account. Instant access is worth more than a fractional yield gain.'],
  freedom:    ['A money-market fund or short-duration bond ETF. Yield above savings, capital-preserving.'],
  home:       ['A savings account for near-term buys. A diversified equity ETF for horizons above 5 years.'],
  education:  ['A diversified equity index fund. Long horizons reward equity — 5+ years is the threshold.'],
  family:     ['Term life for the income earner plus a liquid buffer. Protection first.'],
  travel:     ['A dedicated savings account or bucket. Ring-fence from month-to-month spend.'],
  sabbatical: ['A short-duration bond fund or high-yield savings. Access matters more than yield when you pause.'],
  business:   ['A separate operating account plus a money-market fund for surplus. Keep runway visible.'],
  retire:     ['A tax-anchored retirement account plus broad-market index funds.'],
  fi:         ['A broad-market equity index fund in tax-anchored accounts. FI is the long compound — buy the market.'],
};
function suggestionsFor(country, kind) {
  const c = (country || '').toUpperCase();
  return (INSTRUMENT_SUGGESTIONS[c] && INSTRUMENT_SUGGESTIONS[c][kind])
      || GENERIC_SUGGESTIONS[kind]
      || null;
}

// Wizard step model — visual guide only. All fields remain visible per
// step so users can jump around; gating adds friction without value.
const WIZARD_STEPS = [
  { id: 1, label: 'Direction' },
  { id: 2, label: 'Plan' },
  { id: 3, label: 'Link' },
];

export default function Goals({ embedded = false }) {
  usePageTitle(embedded ? 'Accounts' : 'Goals');
  const { user, profile, session } = useAuth();
  const plan = effectivePlan(profile);
  const currency = profile?.currency || 'INR';

  const uid = user?.id || null;
  const cachedItems = uid ? readCache(uid, 'goals:v2') : null;
  const [items, setItems] = useState(cachedItems || []);
  const [loading, setLoading] = useState(!cachedItems);
  const hasLoadedOnceRef = useRef(!!cachedItems);

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [eligibleLiabilities, setEligibleLiabilities] = useState([]);
  const [loadingLiabilities, setLoadingLiabilities] = useState(false);
  const [breakdown, setBreakdown] = useState(null);

  // Phase 84.28.2 v3.6.4 — Wizard: linkage step state.
  //   linkableAccounts / linkableHoldings — user's own instruments,
  //     lazy-loaded when the dialog opens for a manual (non-linked) template.
  //   wizardLinks — array of { instrument_kind, instrument_id } picked in
  //     the Link step. Sent to PUT /api/goals/{id}/links after the
  //     goal row is created.
  //   wizardStep — currently a visual indicator only; content is fully
  //     visible per step so users can scroll around without gating.
  const [linkableAccounts, setLinkableAccounts] = useState([]);
  const [linkableHoldings, setLinkableHoldings] = useState([]);
  const [loadingLinkables, setLoadingLinkables] = useState(false);
  const [wizardLinks, setWizardLinks] = useState([]);
  const [wizardStep, setWizardStep] = useState(1);
  const country = (profile?.country || '').toUpperCase();

  // Phase 84.27 v3.6.0 — onboarding + celebration modal state.
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [celebrateGoal, setCelebrateGoal]   = useState(null);

  // Fire onboarding modal whenever the user lands here and has zero
  // active goals. Runs on every visit (not once-per-user) per spec.
  // v3.6.2 fix — Only fire AFTER a successful load. Otherwise a transient
  // Supabase 500 leaves items=[] and the modal pops for users who
  // actually have goals.
  useEffect(() => {
    if (loading) return;
    if (!hasLoadedOnceRef.current) return;
    const active = (items || []).filter((g) => (g.status || 'active') === 'active');
    if (active.length === 0 && !open) {
      setOnboardingOpen(true);
    }
  }, [loading, items, open]);

  // Fire celebration modal whenever ANY loaded goal reports
  // `celebration_pending`. We show one goal at a time — pick the first
  // pending one; subsequent ones fire after the user dismisses this.
  // v3.6.2 fix — Once we've shown a modal for a goal id, remember it in
  // a ref so we don't reopen the same one while items[] is stale
  // between the celebrate POST and the next refetch.
  const acknowledgedRef = React.useRef(new Set());
  useEffect(() => {
    if (loading || celebrateGoal) return;
    const pending = (items || []).find((g) =>
      g.progress?.celebration_pending && !acknowledgedRef.current.has(g.id)
    );
    if (pending) setCelebrateGoal(pending);
  }, [loading, items, celebrateGoal]);

  const dismissCelebration = React.useCallback(() => {
    if (celebrateGoal?.id) acknowledgedRef.current.add(celebrateGoal.id);
    setCelebrateGoal(null);
  }, [celebrateGoal]);

  // Phase 72.0 — form now carries `link_kind` + `linked_liability_id`.
  // Phase 72.0.1 — `template_kind` is independent of `title`, so the
  // chip selection stays highlighted even after the user personalises
  // the title (e.g. "Education Savings - Zoe"). Also adds `strategy`
  // for Debt Freedom and `extra_amount` for "lump-sum" or "increase EMI".
  const initialForm = () => ({
    title: '',
    description: '',
    kind: 'custom',
    template_kind: '',
    link_kind: 'manual',
    linked_liability_id: '',
    strategy: 'just_track',
    extra_amount: '',
    months_target: 6,           // Phase 72.1 — Emergency Fund picker
    target_amount: '',
    target_date: '',
    current_amount: '',
    currency,
  });
  const [form, setForm] = useState(initialForm());
  // Phase 72.0.1 — Edit mode: when set, the dialog operates as Edit-on-goal.
  const [editingId, setEditingId] = useState(null);

  // Phase 72.1 — live signals (expense_signal + fi_snapshot). Loaded on
  // mount and again whenever the dialog opens for a linked-EF or linked-FI
  // template, so the form can show "6 months × ₹45k = ₹2.7L" before save.
  const [signals, setSignals] = useState(null);
  const [signalsLoading, setSignalsLoading] = useState(false);

  const isLinkedDebt      = form.link_kind === 'debt_freedom';
  const isLinkedEmergency = form.link_kind === 'emergency_fund';
  const isLinkedFI        = form.link_kind === 'financial_independence';

  // -------------------------------------------------------------------------
  // Loaders
  // -------------------------------------------------------------------------
  const loadBreakdown = async () => {
    if (!session?.access_token) return;
    const d = await fetchGoalsBreakdown(session.access_token);
    if (d) setBreakdown(d);
  };

  const loadV2 = async ({ silent = false } = {}) => {
    if (!silent && !hasLoadedOnceRef.current) setLoading(true);
    try {
      const token = session?.access_token;
      if (!token) {
        // Auth not ready yet — try the legacy Supabase path so the cache
        // hydrates anyway. Legacy goals will still appear; they just
        // won't have the `progress` decoration yet.
        const res = await withTimeout(
          supabase.from('goals').select('*').order('created_at', { ascending: false }),
          12000, 'goals.list',
        );
        if (Array.isArray(res?.data)) {
          setItems(res.data.map((g) => ({ ...g, link_kind: g.link_kind || 'manual', progress: null })));
        }
        return;
      }
      const r = await axios.get(`${BACKEND_URL}/api/goals/v2/list`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 12000,
      });
      const goals = Array.isArray(r.data?.goals) ? r.data.goals : [];
      // Sort: focus first, then completed last, then by created_at desc.
      goals.sort((a, b) => {
        if ((a.focus ? 1 : 0) !== (b.focus ? 1 : 0)) return a.focus ? -1 : 1;
        const aDone = a.progress?.status === 'completed' ? 1 : 0;
        const bDone = b.progress?.status === 'completed' ? 1 : 0;
        if (aDone !== bDone) return aDone - bDone;
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      });
      setItems(goals);
      if (uid) writeCache(uid, 'goals:v2', goals);
      hasLoadedOnceRef.current = true;
    } catch (e) {
      console.warn('Goals v2 load failed (keeping prior data):', e?.message || e);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadEligibleLiabilities = async () => {
    if (!session?.access_token) return;
    setLoadingLiabilities(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/goals/v2/liabilities-eligible`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
        timeout: 10000,
      });
      setEligibleLiabilities(Array.isArray(r.data?.liabilities) ? r.data.liabilities : []);
    } catch (e) {
      console.warn('liabilities-eligible load failed:', e?.message || e);
      setEligibleLiabilities([]);
    } finally {
      setLoadingLiabilities(false);
    }
  };

  // Phase 84.28.2 — load user's own accounts + wealth_holdings so the
  // Link step can offer real, concrete pickers alongside the country-
  // aware suggestions. Direct Supabase reads keep the roundtrip short;
  // RLS on both tables enforces user_id scoping already.
  const loadLinkables = async () => {
    if (!user?.id) return;
    setLoadingLinkables(true);
    try {
      const [accs, whs] = await Promise.all([
        withTimeout(
          supabase.from('accounts')
            .select('id, name, type, currency')
            .order('name'),
          10000, 'goals.linkables.accounts',
        ),
        withTimeout(
          supabase.from('wealth_holdings')
            .select('id, name, kind, currency, value')
            .order('name'),
          10000, 'goals.linkables.holdings',
        ),
      ]);
      setLinkableAccounts(Array.isArray(accs?.data) ? accs.data : []);
      setLinkableHoldings(Array.isArray(whs?.data) ? whs.data : []);
    } catch (e) {
      console.warn('linkables load failed:', e?.message || e);
    } finally {
      setLoadingLinkables(false);
    }
  };

  // Phase 72.1 — fetch live signals (rolling expense avg + FI snapshot)
  // for the create/edit form preview. Never blocks — falls back to a
  // null payload so the form renders calmly even on fresh accounts.
  const loadSignals = async () => {
    if (!session?.access_token) return;
    setSignalsLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/goals/v2/signals`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
        timeout: 10000,
      });
      setSignals(r.data || null);
    } catch (e) {
      console.warn('goals signals load failed:', e?.message || e);
      setSignals(null);
    } finally {
      setSignalsLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      loadV2();
      loadBreakdown();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useFocusRefetch(() => { loadV2({ silent: true }); loadBreakdown(); }, { minHiddenMs: 60_000 });

  // Lazy-load liabilities when the Debt Freedom template is selected.
  useEffect(() => {
    if (isLinkedDebt && open && eligibleLiabilities.length === 0 && !loadingLiabilities) {
      loadEligibleLiabilities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLinkedDebt, open]);

  // Phase 72.1 — lazy-load expense + FI signals when an EF or FI
  // template is selected (or when the dialog is opened in edit-mode on
  // such a goal). Loading is idempotent: we don't re-fetch if we
  // already have signals in this dialog session.
  useEffect(() => {
    if ((isLinkedEmergency || isLinkedFI) && open && !signals && !signalsLoading && session?.access_token) {
      loadSignals();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLinkedEmergency, isLinkedFI, open, session?.access_token]);

  // Phase 84.28.2 — lazy-load accounts + wealth_holdings for the Link
  // step. Only fires for manual (non-linked) templates — Debt / EF / FI
  // are auto-linked to their source instrument and have no picker.
  const isManualTemplate = !isLinkedDebt && !isLinkedEmergency && !isLinkedFI;
  useEffect(() => {
    if (isManualTemplate && open
        && linkableAccounts.length === 0 && linkableHoldings.length === 0
        && !loadingLinkables) {
      loadLinkables();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManualTemplate, open]);

  // -------------------------------------------------------------------------
  // Save / delete / focus
  // -------------------------------------------------------------------------
  const limit = SOFT_LIMITS.goals[plan] || 1;
  const atLimit = items.filter((g) => g.status !== 'archived').length >= limit;

  const reset = () => setForm(initialForm());

  const applyTemplate = (t) => setForm((f) => ({
    ...f,
    // Phase 72.0.1 — only prefill the title if it's still blank or
    // matches the previously-selected template. This lets the user
    // personalise the title ("Education Savings → Zoe") without
    // losing the template selection. The chip stays highlighted because
    // template_kind is tracked independently of the title.
    title: (!f.title || f.title === currentTemplateTitle(f.template_kind)) ? t.title : f.title,
    kind: t.kind,
    template_kind: t.kind,
    link_kind: t.link_kind || 'manual',
    // Reset linked-only fields when switching templates so stale picks
    // don't leak into the next save.
    linked_liability_id: t.link_kind === 'debt_freedom' ? f.linked_liability_id : '',
    months_target: t.link_kind === 'emergency_fund' ? (f.months_target || 6) : 6,
    description: f.description || t.blurb,
  }));

  // Helper: returns the title we'd have set for a given template_kind,
  // used to decide whether the user has personalised the title yet.
  function currentTemplateTitle(kind) {
    if (!kind) return '';
    const t = TEMPLATES.find((x) => x.kind === kind);
    return t?.title || '';
  }

  // Phase 72.0.1 — Edit mode: pre-fill the form from a goal row.
  const openForEdit = (goal) => {
    setEditingId(goal.id);
    const tpl = TEMPLATES.find((t) => t.link_kind === goal.link_kind && t.kind === goal.kind) ||
                TEMPLATES.find((t) => t.kind === goal.kind);
    setForm({
      title: goal.title || '',
      description: goal.description || '',
      kind: goal.kind || 'custom',
      template_kind: tpl?.kind || '',
      link_kind: goal.link_kind || 'manual',
      linked_liability_id: goal.linked_liability_id || '',
      strategy: goal.linked_config?.strategy || 'just_track',
      extra_amount: goal.linked_config?.extra_amount ? String(goal.linked_config.extra_amount) : '',
      months_target: Number(goal.linked_config?.months_target) || 6,
      target_amount: goal.target_amount != null ? String(goal.target_amount) : '',
      target_date: goal.target_date || '',
      current_amount: goal.current_amount != null ? String(goal.current_amount) : '',
      currency: goal.currency || currency,
    });
    setOpen(true);
  };

  const markClosed = async (goal) => {
    // Manual close-out for Debt Freedom (e.g. paid off in cash; loan
    // foreclosed) — flips status to 'completed' so the card visually
    // celebrates and stops nagging with monthly actions.
    try {
      const { error } = await supabase.from('goals').update({ status: 'completed' }).eq('id', goal.id);
      if (error) throw error;
      toast.success('Debt closed. You’ve arrived.');
      loadV2();
    } catch (e) {
      toast.error(e.message || 'Could not mark closed.');
    }
  };

  const save = async (e) => {
    e?.preventDefault?.();
    if (saving) return;

    if (!form.title.trim()) {
      toast.error('Give your goal a name first.');
      return;
    }

    if (isLinkedDebt) {
      // Linked Debt Freedom: target_amount / current_amount come from the
      // liability itself, not from form fields. We just need a liability.
      if (!form.linked_liability_id) {
        toast.error('Pick a loan to track.');
        return;
      }
    } else if (isLinkedEmergency) {
      // Linked Emergency Fund: requires either a live expense signal or
      // a manual fallback target. The picker gives `months_target`; the
      // target itself is derived from rolling expenses.
      const mt = Number(form.months_target);
      if (!mt || mt <= 0) {
        toast.error('Pick how many months of runway you want.');
        return;
      }
    } else if (isLinkedFI) {
      // Linked FI: no inputs required beyond title. The FI engine owns
      // target + projection. We just need confirmation the user has
      // visited the FI page at least once (we surface a guided nudge
      // below if they haven't).
    } else if (!form.target_amount || Number(form.target_amount) <= 0) {
      toast.error('A target amount helps us help you.');
      return;
    }
    // Skip the atLimit check when editing — editing an existing goal
    // shouldn't be blocked by the create-time limit.
    if (atLimit && !editingId) {
      toast.message('You’re at the Clarity goal limit. Horizon unlocks unlimited goals.');
      return;
    }
    setSaving(true);
    try {
      // Phase 72.0.1 / 72.1 — persist strategy + extra_amount + months_target
      // into linked_config based on the link_kind.
      let linked_config = null;
      if (isLinkedDebt) {
        linked_config = {
          strategy: form.strategy || 'just_track',
          extra_amount: form.extra_amount ? Number(form.extra_amount) : null,
        };
      } else if (isLinkedEmergency) {
        linked_config = {
          months_target: Math.max(1, Math.min(24, Number(form.months_target) || 6)),
        };
      } else if (isLinkedFI) {
        // FI has no user-configurable knobs here (those live on the FI
        // tracker page). We persist an empty config for forward-compat.
        linked_config = {};
      }
      let row;
      if (isLinkedDebt) {
        const liab = eligibleLiabilities.find((l) => l.id === form.linked_liability_id);
        row = {
          user_id: user.id,
          title: form.title.trim(),
          description: form.description || null,
          kind: 'debt',
          link_kind: 'debt_freedom',
          linked_liability_id: form.linked_liability_id,
          linked_config,
          target_amount: Number(liab?.principal_amount || liab?.outstanding || 0),
          current_amount: 0,
          target_date: liab?.maturity_date || null,
          monthly_contribution: Math.round(Number(liab?.emi_amount || 0)),
          currency: liab?.currency || form.currency || currency,
          status: 'active',
        };
      } else if (isLinkedEmergency) {
        // Phase 84.28 v3.6.3 — the user's explicit target always wins.
        // Previously the save path overwrote target_amount with
        // monthly_avg × months, which was wrong in two cases:
        //   1. Editing a goal that already had a specific target
        //      (e.g. Priya's demo — AED 120k baked from 20k essentials × 6).
        //   2. Users who type a bespoke number because our monthly_avg
        //      double-counts non-essentials.
        // Auto-derivation is only used when the user hasn't provided one.
        const userTarget = Number(form.target_amount || 0);
        const monthly = Number(signals?.expense_signal?.monthly_avg || 0);
        const months  = Math.max(1, Math.min(24, Number(form.months_target) || 6));
        const snapshotTarget = userTarget > 0
          ? userTarget
          : (monthly > 0 ? Math.round(monthly * months) : 0);
        row = {
          user_id: user.id,
          title: form.title.trim(),
          description: form.description || null,
          kind: 'emergency',
          link_kind: 'emergency_fund',
          linked_config,
          target_amount: snapshotTarget,
          current_amount: Number(form.current_amount || 0),
          target_date: form.target_date || null,
          monthly_contribution: 0,
          currency: form.currency || signals?.expense_signal?.currency || currency,
          status: 'active',
        };
      } else if (isLinkedFI) {
        const fi = signals?.fi_snapshot;
        const fiTarget  = Number(fi?.outputs?.fi_target || 0);
        const fiCorpus  = Number(fi?.outputs?.current_corpus || 0);
        const fiMonthly = Number(fi?.outputs?.monthly_contribution || 0);
        row = {
          user_id: user.id,
          title: form.title.trim(),
          description: form.description || null,
          kind: 'fi',
          link_kind: 'financial_independence',
          linked_config,
          target_amount: fiTarget,
          current_amount: fiCorpus,
          target_date: fi?.outputs?.projected_date || null,
          monthly_contribution: Math.round(fiMonthly),
          currency: fi?.currency || form.currency || currency,
          status: 'active',
        };
      } else {
        const months = monthsUntil(form.target_date) || 12;
        const remaining = Math.max(0, Number(form.target_amount) - Number(form.current_amount || 0));
        const monthly = remaining / months;
        row = {
          user_id: user.id,
          title: form.title.trim(),
          description: form.description || null,
          kind: form.kind,
          link_kind: 'manual',
          target_amount: Number(form.target_amount),
          target_date: form.target_date || null,
          current_amount: Number(form.current_amount || 0),
          monthly_contribution: Math.round(monthly),
          currency: form.currency || currency,
          status: 'active',
        };
      }
      let error;
      let insertedGoalId = null;
      if (editingId) {
        // EDIT — never change user_id; strip it on update.
        // eslint-disable-next-line no-unused-vars
        const { user_id, ...patch } = row;
        const r = await supabase.from('goals').update(patch).eq('id', editingId);
        error = r.error;
      } else {
        // Insert + select — we need the id back so the wizard's Link
        // step can PUT /api/goals/{id}/links immediately after.
        const r = await supabase.from('goals').insert(row).select('id').single();
        error = r.error;
        insertedGoalId = r.data?.id || null;
      }
      if (error) {
        // Phase 72 migration not yet applied → strip the new columns and retry.
        if (/link_kind|linked_liability_id|linked_config|focus/i.test(error.message || '')) {
          const legacyRow = { ...row };
          delete legacyRow.link_kind;
          delete legacyRow.linked_liability_id;
          delete legacyRow.linked_config;
          let r2;
          if (editingId) {
            // eslint-disable-next-line no-unused-vars
            const { user_id, ...patch } = legacyRow;
            r2 = await supabase.from('goals').update(patch).eq('id', editingId);
          } else {
            r2 = await supabase.from('goals').insert(legacyRow).select('id').single();
            insertedGoalId = r2.data?.id || null;
          }
          if (r2.error) throw r2.error;
        } else {
          throw error;
        }
      }
      // Phase 84.28.2 — persist wizard-selected links immediately.
      // Failure here should NOT fail the whole save — the goal itself
      // is fine, only the linkage failed. We surface a warning toast.
      if (!editingId && insertedGoalId && wizardLinks.length > 0 && session?.access_token) {
        try {
          await axios.put(
            `${BACKEND_URL}/api/goals/${insertedGoalId}/links`,
            { links: wizardLinks },
            {
              headers: { Authorization: `Bearer ${session.access_token}` },
              timeout: 10000,
            },
          );
        } catch (linkErr) {
          console.warn('goal links save failed:', linkErr?.message || linkErr);
          toast.warning('Goal saved, but linking the instruments didn’t stick. You can retry from Edit.');
        }
      }
      toast.success(editingId ? 'Goal updated.' : 'Goal added. Steady progress begins here.');
      setOpen(false); setEditingId(null); reset(); setWizardLinks([]); setWizardStep(1); loadV2(); loadBreakdown();
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Could not save goal.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    const { error } = await supabase.from('goals').delete().eq('id', id);
    if (error) return toast.error(error.message);
    toast.success('Goal removed.');
    loadV2(); loadBreakdown();
  };

  const toggleFocus = async (goal) => {
    if (!session?.access_token) return;
    const willFocus = !goal.focus;
    // Optimistic update.
    setItems((arr) => arr.map((g) => {
      if (g.id === goal.id) return { ...g, focus: willFocus };
      if (willFocus && g.focus) return { ...g, focus: false };
      return g;
    }));
    try {
      const path = willFocus ? 'focus' : 'unfocus';
      await axios.post(
        `${BACKEND_URL}/api/goals/v2/${goal.id}/${path}`,
        {},
        { headers: { Authorization: `Bearer ${session.access_token}` }, timeout: 10000 },
      );
      toast.success(willFocus ? `Focus set on ${goal.title}.` : 'Focus cleared.');
      loadV2({ silent: true });
    } catch (e) {
      // Roll back.
      setItems((arr) => arr.map((g) => g.id === goal.id ? { ...g, focus: goal.focus } : g));
      toast.error(e?.response?.data?.detail || 'Could not update focus.');
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {embedded ? (
          <div />
        ) : (
          <div>
            <h1 className="font-display text-2xl font-semibold">Goals</h1>
            <p className="text-sm text-muted-foreground">What are you building toward?</p>
          </div>
        )}
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { reset(); setEditingId(null); setSignals(null); setWizardLinks([]); setWizardStep(1); } }}>
          <DialogTrigger asChild>
            <Button className="rounded-xl" disabled={atLimit} data-testid="goal-add-button">
              <Plus className="h-4 w-4 mr-1" /> Add a goal
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg rounded-2xl">
            <DialogHeader><DialogTitle className="font-display">{editingId ? 'Edit goal' : 'A new direction'}</DialogTitle></DialogHeader>
            {/* Phase 84.28.2 — wizard step indicator (visual only). Hidden
                during edit — the "wizard" is for first-time creation of
                a direction. Clicking a chip scrolls the corresponding
                section into view (no gating). */}
            {!editingId && (
              <div
                className="flex items-center gap-2 -mt-2 mb-1"
                role="list"
                aria-label="Goal creation steps"
                data-testid="goal-wizard-steps"
              >
                {WIZARD_STEPS.map((s, i) => {
                  const active = wizardStep === s.id;
                  return (
                    <React.Fragment key={s.id}>
                      <button
                        type="button"
                        role="listitem"
                        onClick={() => {
                          setWizardStep(s.id);
                          const anchor = document.querySelector(`[data-wizard-section="${s.id}"]`);
                          if (anchor) anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                        data-testid={`goal-wizard-step-${s.id}`}
                        aria-current={active ? 'step' : undefined}
                        className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition-colors ${active
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border/60 bg-card text-muted-foreground hover:text-foreground'}`}
                      >
                        <span className={`inline-flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-semibold ${active
                          ? 'bg-primary-foreground/20 text-primary-foreground'
                          : 'bg-muted text-muted-foreground'}`}>{s.id}</span>
                        {s.label}
                      </button>
                      {i < WIZARD_STEPS.length - 1 && (
                        <span className="h-px flex-1 bg-border/60" aria-hidden="true" />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
            <form onSubmit={save} className="space-y-4">
              <div data-wizard-section="1" onFocus={() => setWizardStep(1)}>
                <Label className="text-xs text-muted-foreground">Inspiration (optional)</Label>
                <div className="mt-1.5 grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-44 overflow-auto pr-1">
                  {TEMPLATES.map((t) => {
                    const Icon = t.icon;
                    const active = form.template_kind === t.kind;
                    return (
                      <button
                        type="button"
                        key={t.kind}
                        onClick={() => applyTemplate(t)}
                        data-testid={`goal-template-${t.kind}`}
                        className={`text-left rounded-xl border px-3 py-2 text-xs transition-colors ${active ? 'border-primary bg-accent text-accent-foreground' : 'border-border/70 hover:border-border bg-card'}`}
                      >
                        <div className="flex items-center gap-1.5 mb-1">
                          <Icon className="h-3.5 w-3.5 text-primary" />
                          {t.linked && (
                            <Link2 className="h-3 w-3 text-primary" aria-label="Linked goal" />
                          )}
                        </div>
                        <div className="font-medium leading-tight">{t.title}</div>
                      </button>
                    );
                  })}
                </div>
                {isLinkedDebt && (
                  <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
                    <Link2 className="h-3 w-3 inline mr-1" />
                    Linked goal — tracks the loan you choose. Target, balance, EMI and payoff date come from your liabilities.
                  </p>
                )}
                {isLinkedEmergency && (
                  <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
                    <Link2 className="h-3 w-3 inline mr-1" />
                    Linked goal — target is derived from your recent spending. If your expenses change, your target updates with you.
                  </p>
                )}
                {isLinkedFI && (
                  <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
                    <Link2 className="h-3 w-3 inline mr-1" />
                    Linked goal — connected to your FI tracker. Target, corpus and timeline live there; adjust assumptions on the FI page.
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label>Title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl" placeholder={isLinkedDebt ? 'e.g. Close car loan' : 'e.g. Emergency cushion'} data-testid="goal-title-input" />
              </div>

              <div className="space-y-1.5">
                <Label>Why this matters (optional)</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl" placeholder="In your own words." />
              </div>

              {/* Linked Debt Freedom: liability picker */}
              <div data-wizard-section="2" onFocus={() => setWizardStep(2)}>
              {isLinkedDebt ? (
                <div className="space-y-1.5">
                  <Label>Which loan?</Label>
                  {loadingLiabilities ? (
                    <Skeleton className="h-10 w-full rounded-xl" />
                  ) : eligibleLiabilities.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-border/70 p-3 text-xs text-muted-foreground" data-testid="goal-no-liabilities">
                      No outstanding loans in Wealth → Liabilities. Add one first and come back.
                    </div>
                  ) : (
                    <div className="space-y-1.5 max-h-44 overflow-auto pr-1" data-testid="goal-liability-picker">
                      {eligibleLiabilities.map((l) => {
                        const active = form.linked_liability_id === l.id;
                        return (
                          <button
                            type="button"
                            key={l.id}
                            onClick={() => setForm({ ...form, linked_liability_id: l.id })}
                            data-testid={`goal-liability-${l.id}`}
                            className={`w-full text-left rounded-xl border px-3 py-2.5 transition-colors ${active ? 'border-primary bg-accent text-accent-foreground' : 'border-border/70 hover:border-border bg-card'}`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                <div className="text-sm font-medium truncate">{l.name}</div>
                                <div className="text-[11px] text-muted-foreground mt-0.5">
                                  {liabilityKindLabel(l.kind)}
                                  {l.emi_amount ? ` · EMI ${formatMoney(l.emi_amount, l.currency || currency, { compact: true })}` : ''}
                                  {l.interest_rate ? ` · ${Number(l.interest_rate).toFixed(2)}%` : ''}
                                </div>
                              </div>
                              <div className="text-right text-xs tabular-nums shrink-0">
                                <div className="font-medium">{formatMoney(l.outstanding, l.currency || currency, { compact: true })}</div>
                                <div className="text-[10px] text-muted-foreground">outstanding</div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Phase 72.0.1 — "What's your plan?" strategy radio.
                      Shown only after a loan is selected, so the choice
                      is anchored to a concrete liability. */}
                  {form.linked_liability_id && (
                    <div className="space-y-1.5 pt-3">
                      <Label>What’s your plan?</Label>
                      <div className="grid grid-cols-1 gap-1.5">
                        {DEBT_STRATEGIES.map((s) => {
                          const active = form.strategy === s.value;
                          return (
                            <button
                              type="button"
                              key={s.value}
                              onClick={() => setForm({ ...form, strategy: s.value })}
                              data-testid={`goal-strategy-${s.value}`}
                              className={`text-left rounded-xl border px-3 py-2 transition-colors ${active ? 'border-primary bg-accent text-accent-foreground' : 'border-border/70 hover:border-border bg-card'}`}
                            >
                              <div className="text-sm font-medium">{s.label}</div>
                              <div className="text-[11px] text-muted-foreground mt-0.5">{s.hint}</div>
                            </button>
                          );
                        })}
                      </div>
                      {(form.strategy === 'increase_emi' || form.strategy === 'one_time_extra') && (
                        <div className="space-y-1.5 pt-1">
                          <Label className="text-xs">
                            {form.strategy === 'increase_emi' ? 'Extra each month' : 'One-time amount'}
                          </Label>
                          <Input
                            type="number"
                            inputMode="decimal"
                            step="0.01"
                            value={form.extra_amount}
                            onChange={(e) => setForm({ ...form, extra_amount: e.target.value })}
                            className="rounded-xl tabular-nums"
                            placeholder="0"
                            data-testid="goal-extra-amount-input"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : isLinkedEmergency ? (
                /* Phase 72.1 — Emergency Fund: months-of-runway picker
                   + live preview of computed target from rolling expense
                   signal. We deliberately keep this simple — the only
                   knob is months_target. */
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm">How many months of runway?</Label>
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="goal-runway-picker">
                      {RUNWAY_MONTHS.map((m) => {
                        const active = Number(form.months_target) === m.value;
                        return (
                          <button
                            type="button"
                            key={m.value}
                            onClick={() => setForm({ ...form, months_target: m.value })}
                            data-testid={`goal-runway-${m.value}`}
                            className={`text-left rounded-xl border px-3 py-2 transition-colors ${active ? 'border-primary bg-accent text-accent-foreground' : 'border-border/70 hover:border-border bg-card'}`}
                          >
                            <div className="text-sm font-medium">{m.label}</div>
                            <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{m.hint}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Live target preview from expense signal */}
                  <EmergencyFundPreview
                    signal={signals?.expense_signal}
                    loading={signalsLoading}
                    monthsTarget={Number(form.months_target) || 6}
                    fallbackCurrency={form.currency || currency}
                  />

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5 col-span-2">
                      <Label className="text-xs">Target amount (optional override)</Label>
                      <Input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        value={form.target_amount}
                        onChange={(e) => setForm({ ...form, target_amount: e.target.value })}
                        className="rounded-xl tabular-nums"
                        placeholder="Leave blank to auto-calc from spending"
                        data-testid="goal-ef-target-input"
                      />
                      {(() => {
                        // Phase 84.28.1 — non-destructive suggestion hint.
                        // Shows the auto-computed target so users see the
                        // recommendation without it silently overwriting
                        // whatever they typed above.
                        // Phase 84.28.2 fix — during signals load and when
                        // there's no signal yet, show a friendly placeholder
                        // so the user isn't staring at an empty container.
                        if (signalsLoading) {
                          return (
                            <p
                              className="text-[11px] text-muted-foreground leading-relaxed"
                              data-testid="goal-ef-target-hint-loading"
                            >
                              <Lightbulb className="h-3 w-3 inline mr-1 text-amber-500" />
                              Estimating a suggestion from your spending…
                            </p>
                          );
                        }
                        const monthly = Number(signals?.expense_signal?.monthly_avg || 0);
                        const months  = Math.max(1, Math.min(24, Number(form.months_target) || 6));
                        const hintCurrency = form.currency
                          || signals?.expense_signal?.currency
                          || currency;
                        if (monthly <= 0) {
                          return (
                            <p
                              className="text-[11px] text-muted-foreground leading-relaxed"
                              data-testid="goal-ef-target-hint-empty"
                            >
                              <Lightbulb className="h-3 w-3 inline mr-1 text-amber-500" />
                              Once we&rsquo;ve seen a few weeks of your spending, a suggested target appears here. Type your own for now.
                            </p>
                          );
                        }
                        const suggested = Math.round(monthly * months);
                        return (
                          <p
                            className="text-[11px] text-muted-foreground leading-relaxed"
                            data-testid="goal-ef-target-hint"
                          >
                            <Lightbulb className="h-3 w-3 inline mr-1 text-amber-500" />
                            Suggested: {formatMoney(suggested, hintCurrency, { compact: true })} ({months} × essentials). Leave blank to keep this in sync automatically.
                          </p>
                        );
                      })()}
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Already saved (optional)</Label>
                      <Input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        value={form.current_amount}
                        onChange={(e) => setForm({ ...form, current_amount: e.target.value })}
                        className="rounded-xl tabular-nums"
                        placeholder="0"
                        data-testid="goal-current-amount-input"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Target date (optional)</Label>
                      <Input
                        type="date"
                        min={todayISO()}
                        value={form.target_date}
                        onChange={(e) => setForm({ ...form, target_date: e.target.value })}
                        className="rounded-xl"
                      />
                    </div>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    <Link2 className="h-3 w-3 inline mr-1" />
                    Linked goal — if you leave the target blank, it stays in sync with your spending pattern.
                  </p>
                </div>
              ) : isLinkedFI ? (
                /* Phase 72.1 — Financial Independence: read-only summary
                   from the FI engine snapshot. No user inputs here; the
                   FI page owns assumption tuning. */
                <FinancialIndependencePreview
                  snapshot={signals?.fi_snapshot}
                  loading={signalsLoading}
                  fallbackCurrency={form.currency || currency}
                />
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Target amount</Label>
                    <Input type="number" inputMode="decimal" step="0.01" value={form.target_amount} onChange={(e) => setForm({ ...form, target_amount: e.target.value })} className="rounded-xl tabular-nums" data-testid="goal-target-input" placeholder="0" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Currency</Label>
                    <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                      <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Already saved (optional)</Label>
                    <Input type="number" inputMode="decimal" step="0.01" value={form.current_amount} onChange={(e) => setForm({ ...form, current_amount: e.target.value })} className="rounded-xl tabular-nums" placeholder="0" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Target date (optional)</Label>
                    <Input type="date" min={todayISO()} value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} className="rounded-xl" />
                  </div>
                </div>
              )}
              </div>{/* /wizard-section-2 */}

              {/* Phase 84.28.2 v3.6.4 — Wizard Step 3: Link.
                  Country-aware instrument suggestions + real accounts /
                  wealth-holdings picker. Hidden during edit (users edit
                  linkage through a dedicated flow, not this dialog).
                  Also hidden for auto-linked templates (Debt / EF / FI)
                  because those are linked implicitly to their source.
                  For those, we show a calm "already linked" note so
                  users don't wonder where step 3 went. */}
              {!editingId && (
                <div
                  data-wizard-section="3"
                  onFocus={() => setWizardStep(3)}
                  className="space-y-3 pt-2 border-t border-border/60"
                >
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-xs text-muted-foreground">Link (optional)</Label>
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Step 3</span>
                  </div>
                  {(isLinkedDebt || isLinkedEmergency || isLinkedFI) ? (
                    <div
                      className="rounded-xl border border-border/70 bg-muted/40 p-3 text-xs text-muted-foreground leading-relaxed"
                      data-testid="goal-link-auto"
                    >
                      <Link2 className="h-3.5 w-3.5 inline mr-1 text-primary" />
                      {isLinkedDebt && 'This goal is auto-linked to the loan you picked above — no manual linkage needed.'}
                      {isLinkedEmergency && 'This goal is auto-linked to your rolling spending signal — no manual linkage needed.'}
                      {isLinkedFI && 'This goal is auto-linked to your FI tracker — adjust assumptions on the FI page.'}
                    </div>
                  ) : (
                    <WizardLinkStep
                      goalKind={form.kind}
                      country={country}
                      accounts={linkableAccounts}
                      holdings={linkableHoldings}
                      loading={loadingLinkables}
                      picks={wizardLinks}
                      onTogglePick={(kind, id) => setWizardLinks((prev) => {
                        const exists = prev.some((p) => p.instrument_kind === kind && p.instrument_id === id);
                        return exists
                          ? prev.filter((p) => !(p.instrument_kind === kind && p.instrument_id === id))
                          : [...prev, { instrument_kind: kind, instrument_id: id }];
                      })}
                    />
                  )}
                </div>
              )}

              <DialogFooter className="sm:justify-end gap-2">
                <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="rounded-xl">Cancel</Button>
                <Button type="submit" disabled={saving} className="rounded-xl" data-testid="goal-save-button">
                  {saving ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…</> : (editingId ? 'Save changes' : 'Save direction')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {atLimit && (
        <Card className="rounded-2xl border-[hsl(var(--gold))]/30 bg-[hsl(var(--gold))]/8">
          <CardContent className="p-4 flex items-center justify-between gap-3">
            <div className="text-xs text-muted-foreground leading-relaxed">
              Clarity supports {limit} active goal. Horizon unlocks unlimited goals, useful when you’re tending to several directions at once.
            </div>
            <UpsellPill featureKey="projections" />
          </CardContent>
        </Card>
      )}

      {loading ? (
        <Skeleton className="h-32 rounded-2xl" />
      ) : items.length === 0 ? (
        <Card className="rounded-2xl border-dashed border-border/70">
          <CardContent className="p-10 text-center" data-testid="goals-empty-state">
            <Target className="h-6 w-6 mx-auto text-primary mb-2" />
            <div className="font-display text-lg font-semibold">Your compass</div>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">Goals in 2120 aren’t targets to hit. They’re directions to walk in. Pick one to begin.</p>
            <Button onClick={() => setOpen(true)} className="mt-4 rounded-xl"><Plus className="h-4 w-4 mr-1" /> Add a goal</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {items.map((g) => (
            <GoalCard
              key={g.id}
              goal={g}
              currency={currency}
              onDelete={() => remove(g.id)}
              onToggleFocus={() => toggleFocus(g)}
              onEdit={() => openForEdit(g)}
              onMarkClosed={() => markClosed(g)}
            />
          ))}
        </div>
      )}

      {/* Phase 84.24 — FX consolidated card renders BELOW the individual
          goal cards so the eye lands on each direction first, then on the
          aggregated cross-currency view. */}
      {!loading && items.length > 0 && (
        <FxBreakdownCard
          title="Goals consolidated"
          base={breakdown?.base_currency || currency}
          breakdown={flattenBreakdownRows(breakdown?.breakdown, ['target', 'current', 'remaining'])}
          headline={breakdown}
          valueColumns={[
            { key: 'target', label: 'Target', nativeKey: 'native_target', baseKey: 'base_target' },
            { key: 'current', label: 'Saved', nativeKey: 'native_current', baseKey: 'base_current' },
          ]}
          warnings={breakdown?.warnings || []}
          testId="goals-fx-breakdown"
        />
      )}

      {/* Phase 84.27 v3.6.0 — Onboarding modal (empty state) + Celebration modal. */}
      <GoalsOnboardingModal
        open={onboardingOpen}
        onOpenChange={setOnboardingOpen}
        onCreate={() => setOpen(true)}
      />
      <GoalCelebrationModal
        goal={celebrateGoal}
        onClose={dismissCelebration}
      />
    </div>
  );
}


// =============================================================================
// GoalCard — unit-aware progress rendering
// =============================================================================
function GoalCard({ goal, currency, onDelete, onToggleFocus, onEdit, onMarkClosed }) {
  const progress = goal.progress;
  const linkKind = goal.link_kind || 'manual';
  const isDebt = linkKind === 'debt_freedom';
  const isEmergency = linkKind === 'emergency_fund';
  const isFI = linkKind === 'financial_independence';
  const isAnyLinked = isDebt || isEmergency || isFI;
  const isCompleted = goal.status === 'completed' || progress?.status === 'completed';
  const goalCurrency = goal.currency || currency;

  // Phase 84.27 v3.6.0 — Auto-tracked chip. A goal is "auto-tracked"
  // whenever its tracking_mode is anything other than 'manual', OR when
  // it uses a legacy smart-link_kind. Chip copy varies by mode so users
  // can eyeball at a glance whether progress is live or manual.
  const trackingMode = progress?.tracking_mode || goal.tracking_mode || (isAnyLinked ? 'auto' : 'manual');
  const isAutoTracked = trackingMode !== 'manual';
  const trackingLabel = {
    auto:             'Auto-tracked',
    maintain_balance: 'Maintain balance',
    debt_payoff:      'Debt payoff',
  }[trackingMode] || (isAnyLinked ? 'Auto-tracked' : null);

  // Compute display values. If the backend gave us progress, use it; else
  // fall back to the legacy field-based math.
  const pct = progress?.progress_pct != null
    ? Math.min(100, Math.max(0, progress.progress_pct))
    : Math.min(100, Math.round((Number(goal.current_amount || 0) / Math.max(1, Number(goal.target_amount))) * 100));
  const status = progress?.status || (pct >= 100 ? 'completed' : 'on_track');
  const subtitle = progress?.subtitle;
  const action = progress?.monthly_action;
  const projectedDoneIso = progress?.projected_done;
  const meta = progress?.meta || {};

  return (
    <Card
      className={
        'rounded-2xl card-lift relative ' +
        // Phase 84.22.6 — Fully-covered goals get a soft sage tint +
        // an evergreen border so the eye lands on them last (they no
        // longer need action). A CheckCircle2 badge on the top row
        // reinforces the "done" state without an extra row of chrome.
        (isCompleted
          ? 'border-emerald-500/40 bg-emerald-50/60 dark:bg-emerald-950/20 ring-1 ring-emerald-500/20 '
          : goal.focus
            ? 'border-primary/60 ring-2 ring-primary/20 '
            : 'border-border/70 ')
      }
      data-testid="goal-row"
      data-completed={isCompleted ? 'true' : 'false'}
    >
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-display text-lg font-semibold flex items-center gap-2">
              <span className="truncate">{goal.title}</span>
              {isCompleted && (
                <Badge
                  variant="outline"
                  className="rounded-full text-[10px] font-normal border-emerald-500/50 text-emerald-700 dark:text-emerald-400 bg-emerald-500/10"
                  data-testid="goal-completed-badge"
                >
                  <CheckCircle2 className="h-2.5 w-2.5 mr-1 fill-current" /> Complete
                </Badge>
              )}
              {isAnyLinked && (
                <Badge variant="outline" className="rounded-full text-[10px] font-normal border-primary/40 text-primary">
                  <Link2 className="h-2.5 w-2.5 mr-1" /> Linked
                </Badge>
              )}
              {/* Phase 84.27 v3.6.0 — Auto-tracked chip. Renders only for
                  new-model tracking (auto / maintain_balance / debt_payoff)
                  and hidden when it would duplicate the legacy "Linked" pill. */}
              {isAutoTracked && trackingLabel && !isAnyLinked && (
                <Badge
                  variant="outline"
                  className="rounded-full text-[10px] font-normal border-emerald-500/40 text-emerald-700 dark:text-emerald-400 bg-emerald-50/60 dark:bg-emerald-950/20"
                  data-testid={`goal-auto-tracked-chip-${trackingMode}`}
                  title="This goal updates automatically from your linked accounts."
                >
                  <RefreshCw className="h-2.5 w-2.5 mr-1" /> {trackingLabel}
                </Badge>
              )}
              {goal.focus && (
                <Badge variant="outline" className="rounded-full text-[10px] font-normal border-primary/60 text-primary bg-primary/10">
                  <Star className="h-2.5 w-2.5 mr-1 fill-current" /> Focus
                </Badge>
              )}
            </div>
            {goal.description && <div className="text-xs text-muted-foreground mt-0.5">{goal.description}</div>}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="rounded-xl"
              onClick={onToggleFocus}
              aria-label={goal.focus ? 'Clear focus' : 'Set as focus goal'}
              title={goal.focus ? 'Clear focus' : 'Set as focus goal'}
              data-testid="goal-focus-toggle"
            >
              <Star className={`h-4 w-4 ${goal.focus ? 'fill-primary text-primary' : 'text-muted-foreground'}`} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="rounded-xl"
              onClick={onEdit}
              aria-label="Edit goal"
              title="Edit goal"
              data-testid="goal-edit-button"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="rounded-xl"
              onClick={onDelete}
              aria-label="Remove goal"
              data-testid="goal-delete-button"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Headline numbers — unit-aware. */}
        {isDebt ? (
          <DebtHeadline meta={meta} pct={pct} goalCurrency={goalCurrency} />
        ) : isEmergency ? (
          <EmergencyHeadline meta={meta} pct={pct} goalCurrency={goalCurrency} />
        ) : isFI ? (
          <FIHeadline meta={meta} pct={pct} goalCurrency={goalCurrency} />
        ) : (
          <ManualHeadline goal={goal} pct={pct} goalCurrency={goalCurrency} />
        )}

        <Progress value={pct} className="h-1.5" data-testid="goal-progress" />

        {subtitle && (
          <div className="text-xs text-muted-foreground">{subtitle}</div>
        )}

        {/* Projected payoff / target date */}
        {/* Phase 84.27 v3.6.0 — Retroactive nudge for existing manual
            goals. Renders only for goals that could benefit from linking
            (i.e. currently 'manual' AND not already using a legacy
            link_kind). One tap opens the edit modal where the user can
            pick tracking_mode + link instruments. */}
        {trackingMode === 'manual' && !isAnyLinked && !isCompleted && (
          <button
            type="button"
            onClick={onEdit}
            className="w-full text-left rounded-xl border border-dashed border-primary/30 bg-primary/[0.03] hover:bg-primary/[0.06] px-3 py-2 text-[11px] text-muted-foreground transition-colors"
            data-testid="goal-auto-track-nudge"
          >
            <span className="inline-flex items-center gap-1.5">
              <RefreshCw className="h-3 w-3 text-primary" />
              <span>
                <span className="text-foreground font-medium">Auto-track this goal?</span>{' '}
                Link an account or investment and we&rsquo;ll keep the number current for you.
              </span>
            </span>
          </button>
        )}

        {(projectedDoneIso || goal.target_date) && (
          <div className="flex items-center justify-end">
            <Badge variant="secondary" className="rounded-full text-[10px]">
              {projectedDoneIso
                ? `Debt-free ${new Date(projectedDoneIso).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}`
                : `by ${new Date(goal.target_date).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}`}
            </Badge>
          </div>
        )}

        {/* Monthly action line — the differentiator. */}
        {action?.primary && (
          <div
            className="rounded-xl border border-primary/25 bg-primary/[0.05] p-3 text-xs leading-relaxed"
            data-testid="goal-monthly-action"
          >
            <div className="flex items-start gap-2">
              <Lightbulb className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-foreground">{action.primary}</div>
                {action.savings_hint && (
                  <div className="text-muted-foreground mt-0.5">{action.savings_hint}</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Closing whisper — kept from legacy. */}
        {!action?.primary && (
          <div className="text-[11px] text-muted-foreground italic pt-1 border-t border-border/40">
            {isCompleted ? 'You’ve arrived.' :
             pct >= 50 ? 'Halfway there. Keep going.' :
                          'Steady progress toward your goal.'}
          </div>
        )}

        {/* Phase 72.0.1 — "Mark as paid off" close-out for Debt Freedom.
            Shown when the goal isn't already completed. Useful when the
            user pays off the loan outside 2120 (foreclosed, refinanced)
            and wants the card to stop nagging. */}
        {isDebt && !isCompleted && onMarkClosed && (
          <button
            type="button"
            onClick={onMarkClosed}
            className="w-full text-[11px] text-primary hover:underline pt-1 flex items-center justify-center gap-1"
            data-testid="goal-mark-closed-button"
          >
            <CheckCircle2 className="h-3 w-3" /> I’ve paid this off
          </button>
        )}
      </CardContent>
    </Card>
  );
}


function DebtHeadline({ meta, pct, goalCurrency }) {
  const currency = meta.currency || goalCurrency;
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Outstanding</div>
          <div className="text-base font-display font-semibold tabular-nums">
            {formatMoney(meta.outstanding || 0, currency, { compact: true })}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Paid down</div>
          <div className="text-base font-display font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">
            {pct.toFixed(0)}%
          </div>
        </div>
      </div>
      {(meta.emi || meta.annual_rate) && (
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
          {meta.emi > 0 && (
            <span>{formatMoney(meta.emi, currency, { compact: true })}/mo</span>
          )}
          {meta.annual_rate > 0 && (
            <span>{Number(meta.annual_rate).toFixed(2)}% interest</span>
          )}
        </div>
      )}
    </div>
  );
}


function ManualHeadline({ goal, pct, goalCurrency }) {
  return (
    <div className="flex items-baseline justify-between">
      <div className="text-xs text-muted-foreground">{pct}% there</div>
      <div className="text-xs text-muted-foreground tabular-nums">
        {formatMoney(goal.current_amount || 0, goalCurrency, { compact: true })}
        {' / '}
        {formatMoney(goal.target_amount, goalCurrency, { compact: true })}
      </div>
    </div>
  );
}


// =============================================================================
// Phase 72.1 — Emergency Fund + Financial Independence headlines / previews
// =============================================================================

function EmergencyHeadline({ meta, pct, goalCurrency }) {
  const currency = meta.currency || goalCurrency;
  const monthsCovered = Number(meta.months_covered || 0);
  const monthsTarget  = Number(meta.months_target || 6);
  const monthlyAvg    = Number(meta.monthly_avg || 0);
  return (
    <div className="space-y-2" data-testid="goal-headline-emergency">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Saved</div>
          <div className="text-base font-display font-semibold tabular-nums">
            {formatMoney(meta.saved || 0, currency, { compact: true })}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Runway</div>
          <div className="text-base font-display font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">
            {monthsCovered.toFixed(1)}
            <span className="text-xs text-muted-foreground font-normal">
              {' '}/ {monthsTarget} mo
            </span>
          </div>
        </div>
      </div>
      {monthlyAvg > 0 && (
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
          <span>
            Based on {formatMoney(monthlyAvg, currency, { compact: true })}/mo spending
            {meta.preliminary ? ' (preliminary)' : ''}
          </span>
        </div>
      )}
    </div>
  );
}


function FIHeadline({ meta, pct, goalCurrency }) {
  const currency = meta.currency || goalCurrency;
  const fiTarget = Number(meta.fi_target || 0);
  const corpus   = Number(meta.current_corpus || 0);
  const monthly  = Number(meta.monthly_contrib || 0);
  return (
    <div className="space-y-2" data-testid="goal-headline-fi">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Corpus</div>
          <div className="text-base font-display font-semibold tabular-nums">
            {formatMoney(corpus, currency, { compact: true })}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Progress</div>
          <div className="text-base font-display font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">
            {Number(pct || 0).toFixed(1)}%
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
        {fiTarget > 0 && (
          <span>Target {formatMoney(fiTarget, currency, { compact: true })}</span>
        )}
        {monthly > 0 && (
          <span>· {formatMoney(monthly, currency, { compact: true })}/mo</span>
        )}
        {meta.preliminary && (
          <span className="text-amber-600 dark:text-amber-400">preliminary</span>
        )}
      </div>
    </div>
  );
}


// -- Create/Edit dialog previews ---------------------------------------------
function EmergencyFundPreview({ signal, loading, monthsTarget, fallbackCurrency }) {
  if (loading) {
    return <Skeleton className="h-20 rounded-xl" />;
  }
  const monthly  = Number(signal?.monthly_avg || 0);
  const months   = Math.max(1, Number(monthsTarget) || 6);
  const currency = signal?.source && signal?.source !== 'none'
    ? (signal?.currency || fallbackCurrency)
    : fallbackCurrency;

  if (!signal || signal.source === 'none' || monthly <= 0) {
    return (
      <div
        className="rounded-xl border border-dashed border-border/70 p-3 text-xs text-muted-foreground leading-relaxed"
        data-testid="goal-ef-preview-empty"
      >
        We don’t have enough activity yet to estimate your monthly spending.
        Once you add transactions, your target will appear here.
      </div>
    );
  }
  const target = Math.round(monthly * months);
  return (
    <div
      className="rounded-xl border border-primary/25 bg-primary/[0.05] p-3 text-xs leading-relaxed"
      data-testid="goal-ef-preview"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-muted-foreground">
          Based on your recent {formatMoney(monthly, currency, { compact: true })}/mo
          {signal.preliminary ? ' (preliminary)' : ''}
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {months} month{months !== 1 ? 's' : ''} target
          </div>
          <div className="font-display text-base font-semibold tabular-nums">
            {formatMoney(target, currency, { compact: true })}
          </div>
        </div>
      </div>
    </div>
  );
}


function FinancialIndependencePreview({ snapshot, loading, fallbackCurrency }) {
  if (loading) {
    return <Skeleton className="h-32 rounded-xl" />;
  }
  if (!snapshot?.outputs) {
    return (
      <div
        className="rounded-xl border border-dashed border-border/70 p-4 text-center space-y-2"
        data-testid="goal-fi-preview-empty"
      >
        <Mountain className="h-5 w-5 text-primary mx-auto" />
        <div className="text-sm font-medium">No FI projection yet</div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Open the FI tracker once to seed your projection. You can come back here after.
        </p>
        <Button asChild variant="ghost" size="sm" className="rounded-xl text-xs">
          <a href="/fi" data-testid="goal-fi-open-tracker">Open FI tracker</a>
        </Button>
      </div>
    );
  }
  const o = snapshot.outputs;
  const currency = snapshot.currency || fallbackCurrency;
  const fiTarget = Number(o.fi_target || 0);
  const corpus   = Number(o.current_corpus || 0);
  const monthly  = Number(o.monthly_contribution || 0);
  const pct      = Number(o.progress_pct || 0);
  const months   = o.projected_months;
  const years    = months != null ? Math.floor(months / 12) : null;
  return (
    <div
      className="rounded-xl border border-primary/25 bg-primary/[0.05] p-4 text-xs leading-relaxed space-y-2.5"
      data-testid="goal-fi-preview"
    >
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Target</div>
          <div className="font-display text-base font-semibold tabular-nums">
            {formatMoney(fiTarget, currency, { compact: true })}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Corpus</div>
          <div className="font-display text-base font-semibold tabular-nums">
            {formatMoney(corpus, currency, { compact: true })}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 text-muted-foreground flex-wrap">
        <span>{pct.toFixed(1)}% of FI target</span>
        {monthly > 0 && (
          <span>{formatMoney(monthly, currency, { compact: true })}/mo</span>
        )}
        {years != null && years > 0 && (
          <span>~{years} year{years !== 1 ? 's' : ''} to FI</span>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground/80 leading-relaxed pt-1 border-t border-border/40">
        These numbers update automatically as your transactions, holdings and FI assumptions change.
      </p>
    </div>
  );
}


// =============================================================================
// Phase 84.28.2 v3.6.4 — Wizard Step 3 (Link).
// Renders country-aware instrument suggestions AND the user's own
// accounts / wealth-holdings for picking. If the user owns nothing yet,
// we surface a calm scaffold pointing them toward opening a dedicated
// bucket / account — the "no natural linkage" pattern from the plan.
// Education-kind goals get an extra featured nudge for mutual-fund
// style compounding, per the product brief.
// =============================================================================
function WizardLinkStep({
  goalKind, country, accounts, holdings, loading, picks, onTogglePick,
}) {
  const suggestions = suggestionsFor(country, goalKind) || [];
  const isEducation = goalKind === 'education';
  const nothingToLink = !loading && accounts.length === 0 && holdings.length === 0;
  const picked = (kind, id) => picks.some((p) => p.instrument_kind === kind && p.instrument_id === id);
  return (
    <div className="space-y-3" data-testid="goal-link-step">
      {/* Education-first nudge — long horizons reward equity, so we
          highlight mutual funds prominently. Shown ABOVE generic
          suggestions so it never gets buried. */}
      {isEducation && (
        <div
          className="rounded-xl border border-primary/30 bg-primary/[0.06] p-3 flex gap-3"
          data-testid="goal-link-education-nudge"
        >
          <div className="mt-0.5 shrink-0">
            <Star className="h-4 w-4 text-primary" />
          </div>
          <div className="space-y-1 text-xs leading-relaxed">
            <div className="font-medium text-foreground">Link a mutual fund?</div>
            <p className="text-muted-foreground">
              Education is a 5–15 year story. A SIP into a diversified equity mutual fund tends to outrun a savings account by a wide margin over that horizon — and you can pick one you already own below.
            </p>
          </div>
        </div>
      )}

      {/* Country-aware instrument suggestion(s). Static copy — the
          picker below covers concrete instruments. */}
      {suggestions.length > 0 && (
        <div
          className="rounded-xl border border-dashed border-border/70 p-3 text-xs leading-relaxed"
          data-testid="goal-link-suggestions"
        >
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Lightbulb className="h-3.5 w-3.5 text-amber-500" />
            <span className="text-[10px] uppercase tracking-wider font-medium">
              Suggested for {country || 'your country'}
            </span>
          </div>
          <ul className="mt-1.5 space-y-1 text-muted-foreground">
            {suggestions.map((s, i) => (
              <li key={i} data-testid={`goal-link-suggestion-${i}`}>{s}</li>
            ))}
          </ul>
        </div>
      )}

      {/* "No natural linkage" scaffold — surfaced when the user owns
          nothing yet. We route them to /accounts (or /wealth) so they
          leave with a next step rather than a shrug. */}
      {nothingToLink ? (
        <div
          className="rounded-xl border border-border/70 bg-card p-3 space-y-2"
          data-testid="goal-link-empty-scaffold"
        >
          <p className="text-xs text-foreground font-medium">
            No accounts or holdings to link yet.
          </p>
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            When money for this goal needs its own place to live — a savings space, a bucket, an SIP — most people find it easier to open a dedicated account. You can link it here later.
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button asChild variant="outline" size="sm" className="rounded-xl text-xs">
              <a href="/accounts" data-testid="goal-link-open-accounts">Open an account</a>
            </Button>
            <Button asChild variant="ghost" size="sm" className="rounded-xl text-xs">
              <a href="/wealth" data-testid="goal-link-open-wealth">Add a holding</a>
            </Button>
          </div>
        </div>
      ) : loading ? (
        <Skeleton className="h-24 w-full rounded-xl" />
      ) : (
        <div className="space-y-2" data-testid="goal-link-pickers">
          {accounts.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Your accounts
              </div>
              <div className="grid grid-cols-1 gap-1.5 max-h-32 overflow-auto pr-1">
                {accounts.map((a) => {
                  const active = picked('account', a.id);
                  return (
                    <button
                      type="button"
                      key={a.id}
                      onClick={() => onTogglePick('account', a.id)}
                      data-testid={`goal-link-account-${a.id}`}
                      aria-pressed={active}
                      className={`text-left rounded-xl border px-3 py-2 transition-colors ${active ? 'border-primary bg-accent text-accent-foreground' : 'border-border/70 hover:border-border bg-card'}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{a.name}</div>
                          <div className="text-[11px] text-muted-foreground mt-0.5">
                            {a.type || 'account'}{a.currency ? ` · ${a.currency}` : ''}
                          </div>
                        </div>
                        {active && <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {holdings.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Your holdings
              </div>
              <div className="grid grid-cols-1 gap-1.5 max-h-32 overflow-auto pr-1">
                {holdings.map((h) => {
                  const active = picked('wealth_holding', h.id);
                  return (
                    <button
                      type="button"
                      key={h.id}
                      onClick={() => onTogglePick('wealth_holding', h.id)}
                      data-testid={`goal-link-holding-${h.id}`}
                      aria-pressed={active}
                      className={`text-left rounded-xl border px-3 py-2 transition-colors ${active ? 'border-primary bg-accent text-accent-foreground' : 'border-border/70 hover:border-border bg-card'}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{h.name}</div>
                          <div className="text-[11px] text-muted-foreground mt-0.5">
                            {h.kind || 'holding'}{h.currency ? ` · ${h.currency}` : ''}
                          </div>
                        </div>
                        {active && <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {picks.length > 0 && (
            <p className="text-[11px] text-muted-foreground leading-relaxed pt-1">
              <Link2 className="h-3 w-3 inline mr-1" />
              {picks.length} instrument{picks.length === 1 ? '' : 's'} linked. Your goal will auto-pull balance updates from {picks.length === 1 ? 'it' : 'them'}.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
